import React, {
  useEffect,
  useState,
} from 'react';
import {
  Input,
  Button,
  FormItem
} from '@mayo/mayo-ui-gakki';
import SubHeader from '@gakki/components/SubHeader';
import { Panel } from './common';
import {
  ResetDialog,
  ResetSuccessDialog,
} from '@gakki/components/Dialog';
import {ReactComponent as CloseIcon} from '@common/assets/icon_times.svg';
import {ReactComponent as CirclePlus} from '@gakki/assets/circle_plus.svg';
import {ReactComponent as EmptyPic} from '@common/assets/img_empty_data.svg';
import {ReactComponent as AngleRightIcon} from '@common/assets/angle-right.svg';
import {ReactComponent as TrashIcon} from '@gakki/assets/trash.svg';
import {ReactComponent as RedoIcon} from 'app/gakki/assets/redo.svg';
import JobLevelForm, { JobLevelFormProps } from './job-level-form';
export type JobGrade = {
  name: string;
  jobLevels: string[];
}

type JobGradeFormProps = {
  jobGrades: JobGrade[];
  setJobGrades: (jobGrades: JobGrade[]) => void;
  isLoading: boolean;
  isFormExpand: boolean;
  setIsFormExpand: (isFormExpand: boolean) => void;
}

const JobGradeRow = (props: JobLevelFormProps & JobGrade & { handleRemove: () => void; idx: number }) => {
  const { name, handleRemove, jobLevels, setJobLevels, idx } = props;
  const [isExpanded, setIsExpanded] = useState(false)
  return (
    <>
      <div className="border-black-300 border-b-[1px] h-10 px-4 flex items-center justify-between gap-[5px]" data-test-id={`job-grade-row_${idx}`}>
        <div className="flex items-center gap-[5px] flex-1">
          <Button
            variant={'simple'}
            colorSchema={'primary'}
            onClick={() => setIsExpanded(prev => !prev)}
            aria-label="expand-job-grade"
            icon={<AngleRightIcon className={`transition-all ${isExpanded ? 'rotate-90' : 'rotate-0'}`}/>}
          />
          <div className="flex-1">{name}</div>
        </div>
        <Button
          variant={'simple'}
          colorSchema={'primary'}
          onClick={handleRemove}
          aria-label="delete-job-grade"
          icon={<TrashIcon/>}
        />
      </div>
      {
        isExpanded &&
        <JobLevelForm
          idx={idx}
          jobLevels={jobLevels}
          setJobLevels={setJobLevels}
        />
      }
    </>
  );
}
export default function JobGradeForm(props: JobGradeFormProps) {
  const {
    jobGrades,
    setJobGrades,
    isLoading,
    isFormExpand,
    setIsFormExpand,
  } = props;
  const [inputValue, setInputValue] = useState(''); 
  const [errorMessage, setErrorMessage] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showResetSuccessDialog, setShowResetSuccessDialog] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setIsFormExpand(jobGrades?.length > 0);
    }
  }, [isLoading, jobGrades, setIsFormExpand])

  const handleAdd = () => {
    const isRepeat = jobGrades.find(s => s.name === inputValue);
    if (inputValue && !isRepeat) {
      const newData = [...jobGrades, {
        name: inputValue,
        jobLevels: [],
      }];
      setJobGrades(newData);
      setInputValue('');
      setErrorMessage('');
    } else {
      if (isRepeat) {
        return setErrorMessage('職等不可相同');
      }
    }
  };

  const handleRemove = (i: number) => {
    const newData = [...jobGrades.slice(0, i), ...jobGrades.slice(i + 1, jobGrades.length)];
    setJobGrades(newData);
  };

  const handleReset = () => {
    setJobGrades([]);
    setShowResetDialog(false);
    setShowResetSuccessDialog(true);
  }

  const syncJobLevels = (currentIdx: number, newJobLevels: string[]) => {
    setJobGrades(jobGrades.map((g, idx) => {
      if (currentIdx === idx) {
        g.jobLevels = newJobLevels
      }
      return g
    }));
  }
  if (!isFormExpand) {
    return (
      <div className="w-[190px] h-[190px] rounded bg-white flex justify-center items-center flex-col cursor-pointer hover:text-primary" onClick={() => setIsFormExpand(true)}>
        <CirclePlus className="w-[55px] h-[55px]"/>
        <span className="text-center text-black">
          新增職等、職級<br/>
          (非必填)
        </span>
      </div>
    )
  }
  return (
    <Panel>
      <SubHeader
        className={'!h-[66.3px] !m-0'}
        rightSide={
          <Button
            variant={'simple'}
            colorSchema={'primary'}
            icon={<CloseIcon/>}
            onClick={() => setIsFormExpand(false)}
          />
        }
      >
        新增職等、職級
      </SubHeader>
      <div className={'grid grid-cols-[1fr,auto] gap-[15px] py-5'}>
        <FormItem errorMessage={Boolean(errorMessage) ? errorMessage : ''}>
          <Input
            onChange={e => {
              setInputValue(e.currentTarget?.value)
              if (!e.currentTarget?.value) {
                setErrorMessage('');
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                handleAdd();
              }
            }}
            value={inputValue}
            isError={Boolean(errorMessage)}
            placeholder="請輸入職等 (非必填)"
            data-test-id={'job-grade-input'}
          />
        </FormItem>
        <Button
          aria-label='add-job-grade'
          variant={'solid'}
          colorSchema={'primary'}
          onClick={handleAdd}
          className={'add-btn whitespace-nowrap'}
        >
          新增
        </Button>
      </div>
      <div className="border-black-300 border-[1px] flex-1">
        <div className="bg-black-300 h-10 flex items-center justify-between px-4">
          <div className="leading-5">職等</div>
          {jobGrades.length > 0 &&
            <Button
              variant={'simple'}
              colorSchema={'primary'}
              onClick={() => setShowResetDialog(true)}
              aria-label="reset-job-grade"
              icon={<RedoIcon />}
            >
              全部重設
            </Button>
          }
        </div>
        {
          jobGrades.length === 0 ?
          <div className="flex flex-col items-center justify-center w-full h-[calc(100%-40px)]">
            <EmptyPic/>
            無資料顯示
          </div> :
          jobGrades.map((jobGrade, idx) => {
            return (
              <JobGradeRow
                {...jobGrade}
                key={`jobGrade_${idx}`}
                idx={idx}
                handleRemove={() => handleRemove(idx)}
                setJobLevels={(newJobLevels: string[]) => syncJobLevels(idx, newJobLevels)}
              />
            )
          })
        }
      </div>
      <ResetDialog
        isOpen={showResetDialog}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={handleReset}
      />
      <ResetSuccessDialog
        isOpen={showResetSuccessDialog}
        onCancel={() => setShowResetSuccessDialog(false)}
      />
    </Panel>
  );
}
